import { memo, type FC, type ReactNode } from "react";
import { InfoCircleFilled } from "@ant-design/icons";
import { Mail, MessageCircle } from "lucide-react";
import type { SegmentPreview } from "../CreateSegment.Constants";
import type { SegmentPreviewStatus } from "../UseSegmentPreview";
import { useSegmentFormatters, useSegmentLabel } from "../UseSegmentLabel";

const PERCENT = 100;

interface ReachStatProps {
  channel: "email" | "whatsapp";
  icon: ReactNode;
  value: number;
  total: number;
}

const ReachStat: FC<ReachStatProps> = ({ channel, icon, value, total }) => {
  const label = useSegmentLabel();
  const { formatValue } = useSegmentFormatters();
  const percent = total ? Math.round((value / total) * PERCENT) : 0;
  const title = label(`matching.${channel}`);

  return (
    <div className={`segment-builder__reach segment-builder__reach--${channel}`}>
      <span className="segment-builder__reach-heading">
        <span className="segment-builder__reach-icon" aria-hidden>
          {icon}
        </span>
        {title}
      </span>
      <span className="segment-builder__reach-values">
        <strong className="segment-builder__reach-value">{formatValue(value)}</strong>
        <span className="segment-builder__reach-share">
          {label("matching.canBeReached", { percent: formatValue(percent) })}
        </span>
      </span>
      <progress className="segment-builder__reach-bar" value={percent} max={PERCENT} aria-label={title} />
    </div>
  );
};

interface MatchingCustomersCardProps {
  preview: SegmentPreview | null;
  status: SegmentPreviewStatus;
  summary: string;
}

const PLACEHOLDER_KEYS: Record<SegmentPreviewStatus, string> = {
  idle: "placeholder",
  loading: "loading",
  error: "error",
  ready: "placeholder",
};

const MatchingCustomersCard: FC<MatchingCustomersCardProps> = ({ preview, status, summary }) => {
  const label = useSegmentLabel();
  const { formatValue } = useSegmentFormatters();

  if (!preview) {
    const key = PLACEHOLDER_KEYS[status];
    return (
      <section
        className="campaign-flow__card segment-builder__card segment-builder__placeholder"
        role={status === "error" ? "alert" : undefined}
        aria-live="polite"
      >
        <InfoCircleFilled className="segment-builder__placeholder-icon" aria-hidden />
        <div>
          <p className="segment-builder__placeholder-title">{label(`matching.${key}Title`)}</p>
          <p className="segment-builder__placeholder-text">{label(`matching.${key}Text`)}</p>
        </div>
      </section>
    );
  }

  return (
    <section
      className="campaign-flow__card segment-builder__card"
      aria-live="polite"
      aria-busy={status === "loading"}
    >
      <div className="segment-builder__matching">
        <div className="segment-builder__matching-total">
          <span className="segment-builder__matching-label">{label("matching.title")}</span>
          <strong className="segment-builder__matching-value">{formatValue(preview.people)}</strong>
        </div>
        <ReachStat channel="email" icon={<Mail size={14} />} value={preview.email} total={preview.people} />
        <ReachStat
          channel="whatsapp"
          icon={<MessageCircle size={14} />}
          value={preview.whatsapp}
          total={preview.people}
        />
      </div>
      <p className="segment-builder__matching-summary">{summary}</p>
    </section>
  );
};

export default memo(MatchingCustomersCard);
